import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Tooltip } from '@mui/material'
import { uploadModes } from '../../constants/inventoryModes'
import UploadResult from './UploadResult'

const ITEMS_TEMPLATE = {
  columns: [
    { name: 'SKU', sample: 'INS-CAF-MOL-1KG', hint: 'Identificador unico del SKU del item.' },
    { name: 'Nombre', sample: 'Cafe molido 1 kg', hint: 'Nombre comercial o descriptivo del item.' },
    { name: 'Unidad', sample: 'KG', hint: 'Unidad base canónica del item. Solo se permite UN, KG o LT.' },
    {
      name: 'Tipo',
      sample: 'Producto',
      tone: 'blue',
      hint: 'Clasifica el item como producto, servicio o con receta.'
    },
    { name: 'Controla', sample: 'Si', hint: 'Indica si el item afecta el stock.' },
    { name: 'Receta', sample: 'No', hint: 'Indica si el item usa receta o estructura BOM.' },
    { name: 'Precio', sample: '$14.990', hint: 'Precio base o referencial del item.' },
    { name: 'Estado', sample: 'Activo', tone: 'green', hint: 'Disponibilidad actual del item en el catalogo.' }
  ],
  note: 'La primera fila del Excel debe traer estos encabezados y respetar este orden.'
}

const INITIAL_STOCK_TEMPLATE = {
  columns: [
    { name: 'SKU', sample: 'INS-AGU-500ML', hint: 'Identificador unico del SKU del item.' },
    { name: 'Item', sample: 'Agua mineral 500 ml', hint: 'Nombre o descripcion visible del item.' },
    { name: 'Bodega', sample: 'BOD-01', hint: 'Codigo o nombre corto de la bodega destino.' },
    { name: 'Cantidad', sample: '38', hint: 'Cantidad de stock inicial que se cargara.' }
  ],
  note: 'La primera fila del Excel debe traer estos encabezados y respetar este orden.'
}

const PreviewPill = ({ value, tone }) => {
  const toneClass =
    tone === 'green'
      ? 'border-green-200 bg-green-100 text-green-700'
      : tone === 'blue'
        ? 'border-blue-200 bg-blue-100 text-blue-700'
        : 'border-slate-200 bg-slate-100 text-slate-700'

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${toneClass}`}>
      {value}
    </span>
  )
}

const PreviewHeaderCell = ({ column }) => (
  <th className='whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700'>
    <Tooltip title={column.hint || column.name} arrow placement='top'>
      <span className='inline-flex cursor-help items-center gap-1.5'>
        <span>{column.name}</span>
        <InfoOutlinedIcon style={{ fontSize: '0.9rem' }} className='text-slate-400' />
      </span>
    </Tooltip>
  </th>
)

const UploadTemplatePreview = ({ eyebrow, title, description, template }) => (
  <div className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'>
    <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
      <div>
        <p className='text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500'>{eyebrow}</p>
        <h4 className='mt-1 text-base font-semibold text-slate-900'>{title}</h4>
        <p className='mt-1 text-sm text-slate-600'>{description}</p>
      </div>
      <span className='inline-flex items-center rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white'>
        Orden exacto
      </span>
    </div>

    <div className='mt-4 overflow-x-auto rounded-xl border border-slate-200'>
      <table className='min-w-full text-sm'>
        <thead className='bg-slate-50'>
          <tr>
            {template.columns.map(column => (
              <PreviewHeaderCell key={column.name} column={column} />
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className='border-t border-slate-200'>
            {template.columns.map(column => (
              <td key={column.name} className='whitespace-nowrap px-4 py-3 text-slate-700'>
                {column.tone ? <PreviewPill value={column.sample} tone={column.tone} /> : column.sample}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>

    <div className='mt-3 flex flex-wrap gap-2'>
      <span className='rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600'>
        Primera fila: encabezados
      </span>
      <span className='rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600'>
        Formato: .xlsx o .xls
      </span>
    </div>

    <p className='mt-3 text-xs leading-4 text-slate-500'>{template.note}</p>
  </div>
)

const UploadPanel = ({ title, description, children }) => (
  <div className='rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 shadow-[0_16px_34px_rgba(242,108,34,0.10)]'>
    <div className='border-b border-orange-200/80 px-4 py-4'>
      <p className='text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-700'>Carga</p>
      <h4 className='mt-1 text-base font-semibold text-slate-900'>{title}</h4>
      <p className='mt-1 text-sm text-slate-600'>{description}</p>
    </div>
    <div className='space-y-4 p-4'>{children}</div>
  </div>
)

const FileField = ({ file, inputId, inputRef, onChange }) => (
  <div className='rounded-xl border border-dashed border-orange-300 bg-white p-4'>
    <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
      <div>
        <p className='text-sm font-semibold text-slate-900'>Archivo Excel</p>
        <p className='mt-1 text-xs text-slate-500'>Acepta archivos .xlsx y .xls.</p>
      </div>
      <label
        htmlFor={inputId}
        className='inline-flex cursor-pointer items-center justify-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800'
      >
        Seleccionar archivo
      </label>
    </div>

    <input
      id={inputId}
      ref={inputRef}
      type='file'
      accept='.xlsx,.xls'
      onChange={onChange}
      className='sr-only'
    />

    <div className='mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600'>
      {file ? `Archivo seleccionado: ${file.name}` : 'Ningun archivo seleccionado'}
    </div>
  </div>
)

const UploadWorkspace = ({ actions, data, navigation, state }) => {
  const isInitialStockMode = navigation.activeUploadMode === 'initial-stock'
  const template = isInitialStockMode ? INITIAL_STOCK_TEMPLATE : ITEMS_TEMPLATE

  return (
    <>
      <div className='mb-4 grid grid-cols-1 gap-2 md:grid-cols-2'>
        {uploadModes.map(mode => {
          const IconComponent = mode.icon
          const isActiveMode = navigation.activeUploadMode === mode.key

          return (
            <button
              key={mode.key}
              type='button'
              onClick={() => navigation.openUploadModal(mode.key)}
              className={`rounded-xl border px-3 py-3 text-left transition ${
                isActiveMode
                  ? 'border-accent2/50 bg-accent2/[0.14] shadow-[0_14px_28px_rgba(28,178,175,0.18)]'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className='flex items-start gap-3'>
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
                    isActiveMode
                      ? 'border-accent2/30 bg-white text-accent2'
                      : 'border-slate-200 bg-slate-50 text-slate-500'
                  }`}
                >
                  <IconComponent style={{ fontSize: '1rem' }} />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isActiveMode ? 'text-slate-900' : 'text-slate-700'}`}>
                    {mode.label}
                  </p>
                  <p className={`mt-1 text-xs ${isActiveMode ? 'text-slate-600' : 'text-slate-500'}`}>
                    {mode.description}
                  </p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <div className='grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.2fr)_400px]'>
        <UploadTemplatePreview
          eyebrow='Formato esperado'
          title={isInitialStockMode ? 'Vista previa de stock inicial' : 'Vista previa de items'}
          description='Toma esta estructura como referencia al preparar el Excel.'
          template={template}
        />

        {isInitialStockMode ? (
          <UploadPanel
            title='Carga stock inicial'
            description='Puedes forzar una bodega para todas las filas o usar la bodega incluida en la planilla.'
          >
            <div className='rounded-xl border border-orange-200/70 bg-white p-4'>
              <label className='mb-1 block text-xs font-semibold text-slate-700'>Bodega destino</label>
              <select
                value={state.initialStockWarehouseId}
                onChange={event => state.setInitialStockWarehouseId(event.target.value)}
                className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              >
                <option value=''>Selecciona una bodega (opcional)</option>
                {data.activeWarehouseOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className='mt-2 text-xs leading-4 text-slate-500'>
                Si la dejas vacia, se usara la bodega indicada en el Excel o la bodega default activa.
              </p>
            </div>

            <FileField
              file={state.initialStockFile}
              inputId='inventory-initial-stock-file'
              inputRef={state.initialStockFileRef}
              onChange={event => state.setInitialStockFile(event.target.files?.[0] || null)}
            />

            <button
              type='button'
              onClick={actions.handleUploadInitialStock}
              disabled={actions.uploadInitialStockMutation.isPending}
              className='w-full rounded-lg bg-[#f26c22] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#d95e1f] disabled:opacity-60'
            >
              {actions.uploadInitialStockMutation.isPending ? 'Procesando...' : 'Cargar stock inicial'}
            </button>

            <UploadResult result={state.initialStockUploadResult} />
          </UploadPanel>
        ) : (
          <UploadPanel title='Carga items' description='Sube el Excel y procesa la importacion del catalogo.'>
            <FileField
              file={state.itemsFile}
              inputId='inventory-items-file'
              inputRef={state.itemsFileRef}
              onChange={event => state.setItemsFile(event.target.files?.[0] || null)}
            />

            <button
              type='button'
              onClick={actions.handleUploadItems}
              disabled={actions.uploadItemsMutation.isPending}
              className='w-full rounded-lg bg-[#f26c22] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#d95e1f] disabled:opacity-60'
            >
              {actions.uploadItemsMutation.isPending ? 'Procesando...' : 'Cargar items'}
            </button>

            <UploadResult result={state.itemsUploadResult} />
          </UploadPanel>
        )}
      </div>
    </>
  )
}

export default UploadWorkspace
